import React, {ReactElement, useCallback, useEffect, useRef, useState} from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import LaunchIcon from '@mui/icons-material/Launch';
import {format} from 'date-fns';
import * as jsondiffpatch from 'jsondiffpatch';
// @ts-ignore
import * as htmlFormatter from 'jsondiffpatch/formatters/html';
import 'jsondiffpatch/formatters/styles/html.css';
import DOMPurify from 'isomorphic-dompurify';
import parse from 'html-react-parser';
import {toast} from 'react-toastify';
import {ComponentHistoryEditorProps} from '../../../../@types/editor-props';
import {Component, ComponentHistorySnapshot, ComponentState, ComponentVersion} from '../../../../@types/component';
import {CanvasActionType} from '../../../../@types/canvas-action-type';
import Group from '../../../../@types/group';
import {ResourceType} from '../../../../@types/resource-type';
import SpringDataPage from '../../../../@types/spring-data-page';
import {useWorkspaceContext} from '../../../../context/workspaceContext';
import componentService from '../../../../services/component.service';
import {canEditComponentDraft} from '../../../../utils/component-utils';
import {componentDiff} from '../../../../utils/component-history-diff';
import ActionDropdown from '../../../form-components/ActionDropdown.component';
import OkCancelModal from '../../../form-components/OkCancelModal.component';
import DiffResource from '../../modals/DiffResource.component';

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];

/** What an open comparison shows: two labelled sides, what changed between them, and what Restore restores. */
interface Comparison {
    earlierLabel: string;
    laterLabel: string;
    delta: jsondiffpatch.Delta | undefined;
    target: ComponentVersion;
}

/**
 * A DRAFT Component's edit history: every change, who made it and when, and what it did. An entry
 * can be compared with the one before it, the current draft or a marked entry, opened read-only,
 * commented, or restored; a restore is recorded as a new entry, so it can be undone.
 */
export default function ComponentHistoryEditor(props: ComponentHistoryEditorProps): ReactElement {
    const {component, isActive} = props;
    const {setCurrentWorkspaceObject, setCurrentCanvasAction, publishResourceUpdate, getAdditionalResource} =
        useWorkspaceContext();
    const [page, setPage] = useState<number>(0);
    const [size, setSize] = useState<number>(ROWS_PER_PAGE_OPTIONS[0]);
    const [history, setHistory] = useState<SpringDataPage<ComponentVersion> | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [restoreTarget, setRestoreTarget] = useState<ComponentVersion | null>(null);
    const [restoreComment, setRestoreComment] = useState<string>('');
    const [commentTarget, setCommentTarget] = useState<ComponentVersion | null>(null);
    const [commentText, setCommentText] = useState<string>('');
    const [markedVersion, setMarkedVersion] = useState<ComponentVersion | null>(null);
    const [comparison, setComparison] = useState<Comparison | null>(null);
    // Snapshots by history entry id: the page's own, and the entry just before the page, which the
    // bottom row's summary is diffed against. Kept across pages; entries never change once made.
    const snapshots = useRef<Map<number, Component>>(new Map());
    const [, setSnapshotsLoaded] = useState<number>(0);

    const groups: Group[] = (getAdditionalResource(ResourceType.GROUPS).resources as Group[]) ?? [];
    const editable: boolean = canEditComponentDraft(component, groups);
    const isDraft: boolean = component.state === ComponentState.DRAFT;

    const load = useCallback((): void => {
        if (!component.id) {
            return;
        }
        setLoading(true);
        componentService.fetchHistory(component.id, page, size, true)
            .then((loaded: SpringDataPage<ComponentVersion>) => {
                loaded.content.forEach((version: ComponentVersion) => {
                    if (version.snapshot) {
                        snapshots.current.set(version.id, version.snapshot);
                    }
                });
                setHistory(loaded);
                const bottom = loaded.content[loaded.content.length - 1];
                if (bottom?.precedingVersionId && !snapshots.current.has(bottom.precedingVersionId)) {
                    snapshotOf(bottom.precedingVersionId)
                        .then(() => setSnapshotsLoaded((count: number) => count + 1))
                        .catch((err: Error) => toast.error(err.message));
                }
            })
            .catch((err: Error) => toast.error(err.message))
            .finally(() => setLoading(false));
    }, [component.id, page, size]);

    // Reloaded whenever the tab comes back into view, so edits made on the canvas meanwhile show up.
    useEffect(() => {
        if (isActive && isDraft) {
            load();
        }
    }, [isActive, isDraft, load]);

    const totalEntries: number = history?.page?.totalElements ?? 0;
    const revisionOf = (index: number): number => totalEntries - page * size - index;

    function snapshotOf(versionId: number): Promise<Component> {
        const cached = snapshots.current.get(versionId);
        if (cached) {
            return Promise.resolve(cached);
        }
        return componentService.getHistoryVersion(component.id, versionId)
            .then((snapshot: Component) => {
                snapshots.current.set(versionId, snapshot);
                return snapshot;
            });
    }

    /** What an entry changed, as HTML, against the entry before it (nothing, for the first). */
    function summaryOf(version: ComponentVersion): string {
        const later = snapshots.current.get(version.id);
        const earlier = version.precedingVersionId ? snapshots.current.get(version.precedingVersionId) : null;
        if (!later || earlier === undefined) {
            return '';
        }
        const delta = componentDiff(earlier, later);
        return delta ? DOMPurify.sanitize(htmlFormatter.format(delta, null)) : '';
    }

    function compareWithPrevious(version: ComponentVersion): void {
        if (!version.precedingVersionId) {
            return;
        }
        Promise.all([snapshotOf(version.precedingVersionId), snapshotOf(version.id)])
            .then(([earlier, later]: Component[]) => setComparison({
                earlierLabel: `Previous (${version.precedingVersionId})`,
                laterLabel: `Selected (${version.id})`,
                delta: componentDiff(earlier, later),
                target: version,
            }))
            .catch((err: Error) => toast.error(err.message));
    }

    function compareWithCurrent(version: ComponentVersion): void {
        // The live draft is what the current entry recorded, wherever that entry is in the list.
        Promise.all([snapshotOf(version.id), componentService.getComponent(component.id)])
            .then(([selected, draft]: Component[]) => setComparison({
                earlierLabel: `Selected (${version.id})`,
                laterLabel: 'Current draft',
                delta: componentDiff(selected, draft),
                target: version,
            }))
            .catch((err: Error) => toast.error(err.message));
    }

    function compareWithMarked(version: ComponentVersion): void {
        if (!markedVersion) {
            return;
        }
        const marked = markedVersion;
        const [earlier, later] = marked.id < version.id ? [marked, version] : [version, marked];
        Promise.all([snapshotOf(earlier.id), snapshotOf(later.id)])
            .then(([earlierSnapshot, laterSnapshot]: Component[]) => setComparison({
                earlierLabel: `${earlier === marked ? 'Marked' : 'Selected'} (${earlier.id})`,
                laterLabel: `${later === marked ? 'Marked' : 'Selected'} (${later.id})`,
                delta: componentDiff(earlierSnapshot, laterSnapshot),
                target: version,
            }))
            .catch((err: Error) => toast.error(err.message));
    }

    function askToRestore(version: ComponentVersion): void {
        setRestoreTarget(version);
        setRestoreComment('');
    }

    const openVersion = (version: ComponentVersion, revision: number): void => {
        componentService.getHistoryVersion(component.id, version.id)
            .then((snapshot) => {
                const tabObject: ComponentHistorySnapshot = {id: version.id, revision, version, component: snapshot};
                setCurrentWorkspaceObject({objectType: ResourceType.COMPONENT_HISTORY_TREE, object: tabObject});
            })
            .catch((err: Error) => toast.error(err.message));
    };

    const restore = (): void => {
        if (!restoreTarget) {
            return;
        }
        const target = restoreTarget;
        setRestoreTarget(null);
        componentService.restoreHistoryVersion(component.id, target.id, restoreComment.trim() || undefined)
            .then((restored) => {
                toast.success(`Restored ${restored.name} to the selected version`);
                publishResourceUpdate({
                    type: ResourceType.COMPONENT,
                    id: restored.id,
                    updatedData: restored,
                    source: 'save',
                });
                // As for safety views: show the restored tree. An already open canvas tab is only
                // switched to, so ask it to reload.
                setCurrentWorkspaceObject({objectType: ResourceType.COMPONENT_TREE, object: restored});
                setCurrentCanvasAction({actionType: CanvasActionType.CANVAS_REFRESH});
            })
            .catch((err: Error) => toast.error(err.message));
    };

    const saveComment = (): void => {
        if (!commentTarget) {
            return;
        }
        const target = commentTarget;
        setCommentTarget(null);
        componentService.tagHistoryVersion(component.id, target.id, commentText.trim())
            .then(() => load())
            .catch((err: Error) => toast.error(err.message));
    };

    if (!isDraft) {
        return (
            <Box sx={{p: 2}}>
                <Alert severity="info">
                    {component.name} is a published release, which is read-only and has no edit history.
                    Changes are made, and recorded, on its draft.
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{height: '100%', p: 1}}>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 0.5}}>
                <Typography variant="h6" component="div">
                    Version History - {component.name} ({component.uniqueId})
                </Typography>
                <TablePagination
                    component="div"
                    count={totalEntries}
                    page={page}
                    onPageChange={(_event: unknown, newPage: number): void => setPage(newPage)}
                    rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                    rowsPerPage={size}
                    onRowsPerPageChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                        setSize(parseInt(event.target.value, 10));
                        setPage(0);
                    }}
                />
            </Box>
            {loading && !history ? (
                <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small" aria-label="component version history">
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Change</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>User</TableCell>
                                <TableCell>Comment</TableCell>
                                <TableCell>Summary</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(history?.content ?? []).length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        No changes recorded yet. Every edit to this draft will appear here.
                                    </TableCell>
                                </TableRow>
                            )}
                            {(history?.content ?? []).map((version: ComponentVersion, index: number) => {
                                const revision = revisionOf(index);
                                return (
                                    <TableRow key={version.id} selected={version.current}>
                                        <TableCell>{revision}</TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <span>{version.action}</span>
                                                {version.current && <Chip label="Current" size="small" color="primary" />}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>{format(new Date(version.created), 'MMMM do, yyyy h:mm a')}</TableCell>
                                        <TableCell>{version.userId}</TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <span>{version.comment}</span>
                                                {editable && (
                                                    <IconButton
                                                        size="small"
                                                        aria-label="edit comment"
                                                        onClick={(): void => {
                                                            setCommentTarget(version);
                                                            setCommentText(version.comment ?? '');
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={{width: 400, verticalAlign: 'top'}}>
                                            <Box sx={{maxHeight: 150, width: 400, overflow: 'auto'}}>
                                                {parse(summaryOf(version))}
                                            </Box>
                                            {version.precedingVersionId && (
                                                <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
                                                    <IconButton
                                                        size="small"
                                                        aria-label="compare with previous"
                                                        onClick={(): void => compareWithPrevious(version)}
                                                    >
                                                        <LaunchIcon fontSize="small" color="primary" />
                                                    </IconButton>
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell align="right" sx={{verticalAlign: 'top'}}>
                                            <ActionDropdown
                                                label="Actions"
                                                onClick={(): void => {}}
                                                actions1={[
                                                    {
                                                        name: 'Compare with Current Draft',
                                                        disabled: version.current,
                                                        action: (): void => compareWithCurrent(version),
                                                    },
                                                    {
                                                        name: 'Compare with Previous Revision',
                                                        disabled: !version.precedingVersionId,
                                                        action: (): void => compareWithPrevious(version),
                                                    },
                                                    {
                                                        name: 'Compare with Marked Revision',
                                                        disabled: markedVersion === null || markedVersion.id === version.id,
                                                        action: (): void => compareWithMarked(version),
                                                    },
                                                ]}
                                                actions2={[
                                                    {
                                                        name: 'Mark For Comparison',
                                                        disabled: markedVersion?.id === version.id,
                                                        action: (): void => {
                                                            setMarkedVersion(version);
                                                            toast.info(`Marked revision ${revision} for comparison`);
                                                        },
                                                    },
                                                    {
                                                        name: 'View',
                                                        disabled: false,
                                                        action: (): void => openVersion(version, revision),
                                                    },
                                                    {
                                                        name: 'Comment',
                                                        disabled: !editable,
                                                        action: (): void => {
                                                            setCommentTarget(version);
                                                            setCommentText(version.comment ?? '');
                                                        },
                                                    },
                                                    {
                                                        name: 'Restore',
                                                        disabled: !editable || version.current,
                                                        action: (): void => askToRestore(version),
                                                    },
                                                ]}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            <DiffResource
                open={comparison !== null}
                resourceA={comparison?.earlierLabel ?? ''}
                resourceB={comparison?.laterLabel ?? ''}
                delta={comparison?.delta}
                onClose={(): void => setComparison(null)}
                handleRestore={(): void => {
                    if (comparison) {
                        askToRestore(comparison.target);
                    }
                }}
                okToRestore={editable && comparison !== null && !comparison.target.current}
            />
            <OkCancelModal
                open={restoreTarget !== null}
                title="Restore version"
                okButtonText="Restore"
                onClose={(): void => setRestoreTarget(null)}
                onConfirm={restore}
                okToConfirm={(): boolean => true}
            >
                <Typography sx={{mb: 2}}>
                    Bring {component.name} back to this version? The restore is recorded as a new
                    version, so it can be undone.
                </Typography>
                <TextField
                    label="Comment (optional)"
                    fullWidth
                    size="small"
                    value={restoreComment}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void => setRestoreComment(event.target.value)}
                />
            </OkCancelModal>
            <OkCancelModal
                open={commentTarget !== null}
                title="Version comment"
                okButtonText="Save"
                onClose={(): void => setCommentTarget(null)}
                onConfirm={saveComment}
                okToConfirm={(): boolean => true}
            >
                <TextField
                    label="Comment"
                    fullWidth
                    size="small"
                    value={commentText}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void => setCommentText(event.target.value)}
                />
            </OkCancelModal>
        </Box>
    );
}
